import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import ExpiryTimer from 'components/Pages/CartReviewPage/ExpiryTimer'
import FlightCartReviewTileExtension from 'components/Pages/CartReviewPage/FlightCartReviewTileExtension'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import { isLeFlightSavedItemData } from 'tripPlanner/api/types'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import CartPanelItemTileLayout from './CartPanelItemTileLayout'
import type { CartPanelFlightItem } from '../hooks/useCartPanelItems'
import { isFlightSearchExpired } from '../utils/flightCartPanelUtils'

interface Props {
  item: CartPanelFlightItem
  onToggleInclude: (item: CartPanelFlightItem) => void
  onChooseFlight: (item: CartPanelFlightItem) => void
  onRefreshFlight: (item: CartPanelFlightItem) => void
  onChangeFlight: (item: CartPanelFlightItem) => void
  onDelete: (item: CartPanelFlightItem) => void
  isStreaming: boolean
}

function getFlightExtensionKey(flight: App.Checkout.FlightItem['flights'][number], legNumber: number): string {
  return [
    `leg-${legNumber}`,
    flight.journeyId,
    flight.journeyKey,
    flight.rewardJourneyKey,
    flight.carrier,
    flight.departingAirportCode,
    flight.arrivalAirportCode,
    flight.departureDateTime,
    flight.arrivalDateTime,
  ].filter(Boolean).join('|')
}

function CartPanelFlightTile({
  item,
  onToggleInclude,
  onChooseFlight,
  onRefreshFlight,
  onChangeFlight,
  onDelete,
  isStreaming,
}: Props) {
  const intl = useIntl()

  const handleDelete = useCallback(() => {
    onDelete(item)
  }, [onDelete, item])

  const handleChooseFlight = useCallback(() => {
    onChooseFlight(item)
  }, [onChooseFlight, item])

  const handleRefreshFlight = useCallback(() => {
    onRefreshFlight(item)
  }, [onRefreshFlight, item])

  const handleChangeFlight = useCallback(() => {
    onChangeFlight(item)
  }, [onChangeFlight, item])

  const routeLabel = useMemo(() => {
    const outbound = item.savedItemData
    const origin = outbound.departureAirport
    const destination = outbound.arrivalAirport
    if (!origin || !destination) return undefined
    return `${origin} to ${destination}`
  }, [item.savedItemData])

  const returnRouteLabel = useMemo(() => {
    const returning = item.returnSavedItemData
    const origin = returning?.departureAirport
    const destination = returning?.arrivalAirport
    if (!origin || !destination) return undefined
    return `${origin} to ${destination}`
  }, [item.returnSavedItemData])

  const statusContent = useMemo(() => {
    if (item.isAvailabilityBlocked) {
      const isPastDate = item.flightCartItem?.status === 'dates_in_past'
      const isUnavailable = item.flightCartItem?.status === 'unavailable'
      const shouldSearchForReplacement = isPastDate || isUnavailable || item.hasRefreshFailed
      return {
        label: isPastDate ? intl.formatMessage({
          id: 'J8r6gZvNBK8Mq4Ny7oOu',
          defaultMessage: 'Flight dates in the past',
          description: 'Attention indicator shown when a TripChat cart flight is for past dates',
        }) : shouldSearchForReplacement ? intl.formatMessage({
          id: '4gHq7Z-820YmOiV_60Z_',
          defaultMessage: 'Flight unavailable',
          description: 'Attention indicator shown when a TripChat cart flight is unavailable',
        }) : intl.formatMessage({
          id: 'OHKYOzwcRPzDdP1VZuy7',
          defaultMessage: 'Flight price expired',
          description: 'Attention indicator shown when a TripChat cart flight is expired or unavailable',
        }),
        banner: isPastDate ? intl.formatMessage({
          id: 'O4ACypR4Y-7wgqg49xMW',
          defaultMessage: 'Your selected dates are in the past.',
          description: 'Warning banner shown when a TripChat cart flight has past dates',
        }) : shouldSearchForReplacement ? intl.formatMessage({
          id: '9jyP-CKrbp3Umb1Mbqta',
          defaultMessage: "We couldn't refresh your flight.",
          description: 'Warning banner shown when a TripChat cart flight cannot be refreshed',
        }) : intl.formatMessage({
          id: 'PWk-7aoXLS6AEnorUvDp',
          defaultMessage: 'This flight price has expired. Refresh the search to continue.',
          description: 'Warning banner shown when a TripChat cart flight is expired',
        }),
        action: shouldSearchForReplacement ? intl.formatMessage({
          id: '_UqqvrffchEPRogC5KWI',
          defaultMessage: 'Search for flights',
          description: 'Button label to search for a replacement TripChat cart flight',
        }) : intl.formatMessage({
          id: 'Jh-CAGCQUEuBbstfiO9X',
          defaultMessage: 'Refresh search',
          description: 'Button label to refresh an expired TripChat cart flight',
        }),
        onAction: shouldSearchForReplacement ? handleChangeFlight : handleRefreshFlight,
      }
    }

    if (item.cartStatus === 'needs_flight_offer') {
      return {
        label: intl.formatMessage({
          id: 'S_d6JoNXtKoCnyK9qaXA',
          defaultMessage: 'Flight offer needed',
          description: 'Attention indicator prompting the user to choose an exact flight offer',
        }),
        action: intl.formatMessage({
          id: 'Pa0mncAzwBO5UPqryVCl',
          defaultMessage: 'Choose flight',
          description: 'Button label to choose an exact flight from a TripChat cart prompt',
        }),
        onAction: handleChooseFlight,
      }
    }

    if (item.cartStatus === 'needs_flight_selection') {
      const isExpired = isLeFlightSavedItemData(item.savedItemData) && isFlightSearchExpired(item.savedItemData.searchExpiry)
      return {
        label: isExpired ? intl.formatMessage({
          id: 'l_qj-dmqmC1QuZQrp6zn',
          defaultMessage: 'Flight price expired',
          description: 'Attention indicator shown when a saved TripChat flight estimate has expired',
        }) : intl.formatMessage({
          id: 'hZtUG-Sni2h8jNqQGfZB',
          defaultMessage: 'Flight selection needed',
          description: 'Attention indicator prompting the user to select an exact flight fare',
        }),
        action: isExpired ? intl.formatMessage({
          id: 'Jh-CAGCQUEuBbstfiO9X',
          defaultMessage: 'Refresh search',
          description: 'Button label to refresh an expired TripChat cart flight',
        }) : intl.formatMessage({
          id: 'Oz_IDhsPwsroLi8fkg3t',
          defaultMessage: 'Select flight',
          description: 'Button label to select an exact fare for a TripChat cart flight',
        }),
        onAction: isExpired ? handleRefreshFlight : handleChooseFlight,
      }
    }

    if (item.cartStatus === 'active') {
      return {
        action: intl.formatMessage({
          id: 'zkkrjGNXRdPMP1Ej6gY4',
          defaultMessage: 'Change flight',
          description: 'Button label to change an active TripChat cart flight',
        }),
        onAction: handleChangeFlight,
      }
    }

    return undefined
  }, [
    handleChangeFlight,
    handleChooseFlight,
    handleRefreshFlight,
    intl,
    item.cartStatus,
    item.flightCartItem?.status,
    item.hasRefreshFailed,
    item.isAvailabilityBlocked,
    item.savedItemData,
  ])

  const deleteLabel = intl.formatMessage({
    id: 'jDEZjhV3eo-cnxIBHOtR',
    defaultMessage: 'Delete',
    description: 'Button to permanently delete an item from the trip planner cart panel',
  })

  const attention = statusContent?.label ? (
    <MessagingLabel variant="warningLight">
      {statusContent.label}
    </MessagingLabel>
  ) : undefined

  const availabilityBanner = statusContent?.banner ? (
    <MessageBanner kind="warning" density="compact" icon={<LineInfoCircleIcon />}>
      {statusContent.banner}
    </MessageBanner>
  ) : undefined

  const activeFlightItem = item.flightCartItem && isFlightItem(item.flightCartItem.item)
    ? item.flightCartItem.item
    : undefined
  const activeFlightExpiry = activeFlightItem?.expiry
  const showCountdown = item.cartStatus === 'active' && !item.isAvailabilityBlocked && activeFlightExpiry !== undefined
  const activeFlightDetails = activeFlightItem?.flights?.map((flight, index) => (
    <FlightCartReviewTileExtension
      key={getFlightExtensionKey(flight, index + 1)}
      flight={flight}
      isClassicReward={activeFlightItem.isClassicReward}
    />
  ))

  const content = (
    <Group direction="vertical" gap={4}>
      <Heading variant="heading5">{item.name}</Heading>
      {routeLabel && <BodyText variant="medium" colour="neutral-three">{routeLabel}</BodyText>}
      {returnRouteLabel && <BodyText variant="medium" colour="neutral-three">{returnRouteLabel}</BodyText>}
      {item.savedItemData.airline && <BodyText variant="medium" colour="neutral-three">{item.savedItemData.airline}</BodyText>}
      {showCountdown && <ExpiryTimer expiry={activeFlightExpiry} canShowExtension />}
      {availabilityBanner}
      {attention}
      {activeFlightDetails}
    </Group>
  )

  const actions = (
    <Group direction="horizontal" gap={8}>
      {statusContent?.action && (
        <TextButton kind="tertiary" size="small" onClick={statusContent.onAction} disabled={isStreaming}>
          {statusContent.action}
        </TextButton>
      )}
      <TextButton kind="tertiary" size="small" startIcon={<LineTrashAltIcon />} onClick={handleDelete} disabled={isStreaming}>
        {deleteLabel}
      </TextButton>
    </Group>
  )

  const price = item.pricing.price !== undefined ? (
    item.cartStatus === 'active' ? (
      <CartReviewPriceStack
        price={item.pricing.price}
        memberPrice={item.pricing.memberPrice}
        value={item.pricing.value ?? 0}
      />
    ) : (
      <PriceRowPrice
        price={item.pricing.price}
        priceFormat="preciseDollar"
        saleUnit=""
        size="S"
      />
    )
  ) : undefined

  return (
    <CartPanelItemTileLayout
      item={item}
      onToggleInclude={onToggleInclude}
      content={content}
      attention={undefined}
      actions={actions}
      price={price}
      isToggleDisabled={item.isAvailabilityBlocked && item.cartExcluded}
    />
  )
}

export default CartPanelFlightTile
